/**
 * Utility to load onboarding steps from module federation (runtime remote inject per product access).
 *
 * @author vikram singh <vikram@maropost.com>
 * @created 2026-02-06
 */

import type { ProductType, ProductAccess } from '@/lib/types/onboarding'
import { loadOnboardingRemoteModule } from '@/lib/config/spa/register-onboarding-remote'

// Cache for each product's onboarding steps (key -> title)
const stepCache: Record<ProductType, Record<string, string> | null> = {
  marketing: null,
  commerce: null,
  service: null,
}

// Loading promises to prevent duplicate requests
const loadingPromises: Record<ProductType, Promise<Record<string, string>> | null> = {
  marketing: null,
  commerce: null,
  service: null,
}

/**
 * Normalizes the onboarding steps to a record of strings
 * @param rawSteps - The raw steps
 * @returns The normalized steps
 */
const normalizeSteps = (rawSteps: unknown): Record<string, string> => {
  if (rawSteps) {
    if (Array.isArray(rawSteps)) {
      return rawSteps.reduce((acc, key) => {
        acc[key] = key
        return acc
      }, {} as Record<string, string>)
    }
    if (typeof rawSteps === 'object') {
      return rawSteps as Record<string, string>
    }
  }
  return {}
}

/**
 * Loads onboarding steps by injecting the product remoteEntry and reading ./onboardingSteps.
 * @param product - The product type (marketing, commerce, or service)
 * @returns Promise that resolves to the onboarding steps record
 */
async function loadFromOnboardingRemote(
  product: ProductType
): Promise<Record<string, string> | null> {
  try {
    const mod = await loadOnboardingRemoteModule(product)
    if (!mod) return null
    const steps = normalizeSteps(mod?.ONBOARDING_STEPS ?? mod?.default)
    return Object.keys(steps).length ? steps : null
  } catch {
    return null
  }
}

/**
 * Loads onboarding steps for a product when that product is enabled in productAccess.
 * @param product - The product type (marketing, commerce, or service)
 * @param productAccess - When provided, remote is loaded only if access[product] is true
 * @returns Promise that resolves to the onboarding steps record
 */
export async function loadOnboardingSteps(
  product: ProductType,
  productAccess?: Partial<ProductAccess>
): Promise<Record<string, string>> {
  if (productAccess !== undefined) {
    const access = getProductAccessWithDefaults(productAccess)
    if (!access[product]) {
      // Do not cache — access may change later; remoteEntry must not load.
      return {}
    }
  }

  if (stepCache[product]) {
    return stepCache[product] as Record<string, string>
  }

  if (loadingPromises[product]) {
    return loadingPromises[product] as Promise<Record<string, string>>
  }

  const promise = (async (): Promise<Record<string, string>> => {
    try {
      const steps = await loadFromOnboardingRemote(product)
      if (steps) {
        stepCache[product] = steps
        return steps
      }
      console.error(`Failed to load onboarding steps for ${product}.`)
    } catch (error) {
      console.error(`Error loading ${product} onboarding steps:`, error)
    }
    const fallback: Record<string, string> = {}
    stepCache[product] = fallback
    return fallback
  })().finally(() => {
    loadingPromises[product] = null
  })

  loadingPromises[product] = promise
  return promise
}

/**
 * Gets onboarding steps synchronously (returns cached value or empty array)
 * Should be called after loadOnboardingSteps() has been awaited at least once
 * @param product - The product type
 * @returns Cached steps or empty array if not loaded yet
 */
export function getOnboardingStepsSync(product: ProductType): Record<string, string> {
  return stepCache[product] || {}
}

/**
 * Default product access (all disabled)
 */
const DEFAULT_PRODUCT_ACCESS: ProductAccess = {
  marketing: false,
  service: false,
  commerce: false,
}

/**
 * Gets product access with defaults
 */
export function getProductAccessWithDefaults(
  productAccess?: Partial<ProductAccess>
): ProductAccess {
  return {
    ...DEFAULT_PRODUCT_ACCESS,
    ...productAccess,
  }
}

/**
 * Gets all onboarding steps by combining enabled products' steps
 * Only includes steps for products that are enabled for the user/account
 * 
 * @param productAccess - Product access flags
 * @returns Promise that resolves to array of onboarding step keys
 */
export async function getAllOnboardingStepsFiltered(
  productAccess?: Partial<ProductAccess>
): Promise<Record<string, string>> {
  const access = getProductAccessWithDefaults(productAccess)
  const promises: Promise<Record<string, string>>[] = []

  for (const [product, isEnabled] of Object.entries(access) as [ProductType, boolean][]) {
    if (isEnabled) {
      promises.push(loadOnboardingSteps(product, access))
    }
  }

  const stepsArrays = await Promise.all(promises)
  return stepsArrays.reduce((acc, steps) => ({ ...acc, ...steps }), {})
}

/**
 * Gets all onboarding steps synchronously (returns cached values or empty arrays)
 * Only includes steps for products that are enabled for the user/account
 * 
 * @param productAccess - Product access flags
 * @returns Array of onboarding step keys
 */
export function getAllOnboardingStepsFilteredSync(
  productAccess?: Partial<ProductAccess>
): Record<string, string> {
  const access = getProductAccessWithDefaults(productAccess)
  const steps: Record<string, string> = {}

  for (const [product, isEnabled] of Object.entries(access) as [ProductType, boolean][]) {
    if (isEnabled) {
      Object.assign(steps, getOnboardingStepsSync(product))
    }
  }

  return steps
}

/**
 * Pre-loads onboarding steps for enabled products only
 * 
 * @param productAccess - Product access flags
 * @returns Promise that resolves when all enabled products' steps are loaded
 */
export async function preloadOnboardingStepsForEnabledProducts(
  productAccess?: Partial<ProductAccess>
): Promise<void> {
  const access = getProductAccessWithDefaults(productAccess)
  const promises: Promise<void>[] = []

  for (const [product, isEnabled] of Object.entries(access) as [ProductType, boolean][]) {
    if (isEnabled) {
      promises.push(loadOnboardingSteps(product, access).then(() => {}))
    }
  }

  await Promise.allSettled(promises)
}

/**
 * Checks if a remote onboarding steps application is accessible.
 * 
 * @param remoteUrl - The remote onboarding steps application URL
 * @returns Promise that resolves to true if accessible, false otherwise
 */
export async function checkRemoteApplicationAvailability(remoteUrl: string): Promise<boolean> {
  try {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 5000)
    
    const response = await fetch(remoteUrl, {
      method: 'HEAD',
      cache: 'no-store',
      signal: controller.signal,
    })
    
    clearTimeout(timeoutId)
    return response.ok
  } catch {
    return false
  }
}
